import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Alert,
  Divider
} from '@mui/material';
import { ContentCopy as CopyIcon, Refresh as RefreshIcon, QrCode2 as QrCodeIcon } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

interface RoomDialogProps {
  open: boolean;
  onClose: () => void;
  onRoomChange?: () => void;
}

export function RoomDialog({ open, onClose, onRoomChange }: RoomDialogProps) {
  const { t } = useTranslation();
  const [currentRoom, setCurrentRoom] = useState<string>('');
  const [newRoom, setNewRoom] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch current room when dialog opens
  useEffect(() => {
    if (open) {
      fetchCurrentRoom();
      setShowQR(false); // Reset QR visibility when dialog opens
    }
  }, [open]);

  // Focus and select text when dialog opens
  useEffect(() => {
    if (open) {
      // Use setTimeout to ensure the dialog is fully rendered and input is available
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const fetchCurrentRoom = async () => {
    try {
      const response = await fetch('/api/room');
      const data = await response.json();
      setCurrentRoom(data.roomId || '');
      setNewRoom(data.roomId || '');
    } catch (err) {
      console.error('Failed to fetch room:', err);
      setError('Failed to fetch current room');
    }
  };

  const handleSetRoom = async () => {
    if (!newRoom.trim()) {
      setError('Room ID cannot be empty');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch('/api/room', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ roomId: newRoom.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to update room');
      }

      const data = await response.json();
      setCurrentRoom(data.roomId);
      setSuccess(true);
      
      // Reload the page to reconnect with new room
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
      if (onRoomChange) {
        onRoomChange();
      }
    } catch (err) {
      console.error('Failed to set room:', err);
      setError('Failed to update room');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('room', currentRoom);
    navigator.clipboard.writeText(url.toString());
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 3000);
  };

  const generateRandomRoom = () => {
    // Generate a random room ID
    const randomId = Math.random().toString(36).substring(2, 15);
    setNewRoom(randomId);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSetRoom();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('Room Settings')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('Share this room ID with others to collaborate in the same session.')}
          </Typography>
          
          {currentRoom && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('Current Room')}:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  value={(() => {
                    const url = new URL(window.location.href);
                    url.searchParams.set('room', currentRoom);
                    return url.toString();
                  })()}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    style: {
                      backgroundColor: '#f5f5f5',
                      color: '#666'
                    }
                  }}
                  size="small"
                />
                <Tooltip title={t('Copy room link')}>
                  <IconButton onClick={handleCopyLink} size="small">
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('Show QR code')}>
                  <IconButton onClick={() => setShowQR(!showQR)} size="small" color={showQR ? 'primary' : 'default'}>
                    <QrCodeIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              
              {showQR && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('Scan to join room')}
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                    <QRCodeSVG 
                      value={(() => {
                        const url = new URL(window.location.href);
                        url.searchParams.set('room', currentRoom);
                        return url.toString();
                      })()}
                      size={200}
                      level="M"
                      includeMargin={true}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('Join or Create Room')}:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                value={newRoom}
                onChange={(e) => setNewRoom(e.target.value)}
                placeholder={t('Enter room ID')}
                fullWidth
                size="small"
                disabled={loading}
                inputRef={inputRef}
                onKeyDown={handleKeyDown}
              />
              <Tooltip title={t('Generate random room')}>
                <IconButton onClick={generateRandomRoom} size="small" disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {t('Room updated! Reloading...')}
            </Alert>
          )}

          {linkCopied && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {t('Room link copied to clipboard!')}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('Cancel')}
        </Button>
        <Button 
          onClick={handleSetRoom} 
          variant="contained" 
          disabled={loading || newRoom === currentRoom || !newRoom.trim()}
        >
          {t('Set Room')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}